import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import type { MenuCategory, MenuCategoryRequest, ReorderRequest } from '../types'

export function useCategories(restaurantId: number) {
  const queryClient = useQueryClient()

  const categoriesQuery = useQuery({
    queryKey: ['menu-categories', restaurantId],
    queryFn: async () => {
      const response = await apiClient.get<MenuCategory[]>(
        `/api/admin/restaurants/${restaurantId}/menu-categories`
      )
      return response.data
    },
    enabled: !!restaurantId,
  })

  const createCategory = useMutation({
    mutationFn: async (data: MenuCategoryRequest) => {
      const response = await apiClient.post<{ id: number }>(
        `/api/admin/restaurants/${restaurantId}/menu-categories`,
        data
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu-categories', restaurantId] })
    },
  })

  const updateCategory = useMutation({
    mutationFn: async ({ categoryId, data }: { categoryId: number; data: MenuCategoryRequest }) => {
      await apiClient.put(`/api/admin/menu-categories/${categoryId}`, data)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu-categories', restaurantId] })
    },
  })

  const deleteCategory = useMutation({
    mutationFn: async (categoryId: number) => {
      await apiClient.delete(`/api/admin/menu-categories/${categoryId}`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu-categories', restaurantId] })
    },
  })

  const reorderCategories = useMutation({
    mutationFn: async (data: ReorderRequest) => {
      await apiClient.post('/api/admin/menu-categories/reorder', data)
    },
    onMutate: async (data) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['menu-categories', restaurantId] })

      // Snapshot previous value
      const previousCategories = queryClient.getQueryData<MenuCategory[]>(['menu-categories', restaurantId])

      // Optimistically update
      if (previousCategories) {
        const reorderedCategories = data.ids.map((id, index) => {
          const category = previousCategories.find(c => c.id === id)!
          return { ...category, orderindex: index * 10 }
        })
        queryClient.setQueryData(['menu-categories', restaurantId], reorderedCategories)
      }

      return { previousCategories }
    },
    onError: (_err, _data, context) => {
      // Rollback on error
      if (context?.previousCategories) {
        queryClient.setQueryData(['menu-categories', restaurantId], context.previousCategories)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu-categories', restaurantId] })
    },
  })

  return {
    categories: categoriesQuery.data ?? [],
    isLoading: categoriesQuery.isLoading,
    error: categoriesQuery.error,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
  }
}
